// Curated managed-agent templates — a PURE data module (no pg/ai/workflow
// deps) shared by the SPA create wizard (template picker) and validated by
// lib/__tests__/agent-templates.test.ts. Each template prefills a brief, mode,
// schedule, memory access, and a tool allowlist.
//
// NO DEAD MODES: every template maps to capabilities that REALLY exist in this
// repo's default safe toolset (see agent-tool-catalog.ts). Every listed tool is
// asserted to be a member of MANAGED_AGENT_DEFAULT_SAFE_TOOL_SET by the test, so
// a template can never promise a tool the agent can't actually run. Templates
// deliberately grant NO connectors (those require an explicit owner grant at
// create time) — they are all read-only web/docs/viz agents out of the box.

import type { AgentMode, MemoryAccessLevel } from '@/lib/managed-agents';

export interface AgentTemplate {
  /** Stable id (used as the picker key). */
  id: string;
  /** Display name — also prefilled as the agent name. */
  name: string;
  emoji: string;
  /** Accent color (hex) for the picker card + agent card. */
  color: string;
  /** One-line pitch shown in the picker. */
  tagline: string;
  mode: AgentMode;
  /** Cron expression, or null for a manual-only agent. */
  schedule: string | null;
  /** Human label for the schedule shown in the picker. */
  scheduleLabel: string;
  /** Prefilled standing brief (the owner edits the {placeholders}). */
  brief: string;
  /** Tool allowlist — a subset of the default safe toolset. */
  toolAllowlist: string[];
  memoryAccess: MemoryAccessLevel;
  /** Suggested daily credit budget. */
  dailyBudgetCredits: number;
}

export const AGENT_TEMPLATES: readonly AgentTemplate[] = [
  {
    id: 'research-digest',
    name: 'Research digest',
    emoji: '📰',
    color: '#8B5CF6',
    tagline: 'A daily web-research digest on the topics you care about.',
    mode: 'digest',
    schedule: '0 9 * * *',
    scheduleLabel: 'Daily at 9am',
    brief:
      'Every morning, run web searches on these topics: {topics}. Synthesize what is genuinely new or important since yesterday into a tight 5-bullet digest, each bullet with a source link. Skip anything you have already reported. If nothing meaningful changed, say so in one line rather than padding.',
    toolAllowlist: ['webSearch', 'viewImages', 'mermaid', 'html'],
    memoryAccess: 'none',
    dailyBudgetCredits: 50,
  },
  {
    id: 'topic-monitor',
    name: 'Topic monitor',
    emoji: '🛰️',
    color: '#06B6D4',
    tagline: 'Watches a topic or source and only pings you when something notable breaks.',
    mode: 'monitor',
    schedule: '0 * * * *',
    scheduleLabel: 'Hourly',
    brief:
      'Watch for new developments about {topic or source}. On each wake, search the web for anything published since your last run. Only report if there is something genuinely notable — a real announcement, incident, or change. Otherwise stay quiet (a heartbeat with no report). When you do report, lead with why it matters and include the source link.',
    toolAllowlist: ['webSearch', 'viewImages'],
    memoryAccess: 'none',
    dailyBudgetCredits: 60,
  },
  {
    id: 'weekly-recap',
    name: 'Weekly recap',
    emoji: '🗓️',
    color: '#22C55E',
    tagline: 'A weekly summary that ties the week together, with a simple chart.',
    mode: 'digest',
    schedule: '0 9 * * 1',
    scheduleLabel: 'Mondays at 9am',
    brief:
      'Every Monday morning, produce a recap of the past week on {subject}. Pull together the notable web developments, summarize the throughline in a short narrative, and render one simple visualization (a timeline or bar chart) of the week. Keep it skimmable: a headline, 3–5 bullets, and the chart.',
    toolAllowlist: ['webSearch', 'viewImages', 'mermaid', 'html', 'listUserMemories', 'viewFullMemory'],
    memoryAccess: 'read',
    dailyBudgetCredits: 80,
  },
  {
    id: 'document-digest',
    name: 'Document digest',
    emoji: '📂',
    color: '#F59E0B',
    tagline: 'Reviews your documents on a schedule and digests what they say.',
    mode: 'digest',
    schedule: '0 8 * * *',
    scheduleLabel: 'Daily at 8am',
    brief:
      'Each morning, review my documents and produce a digest of what matters in {which documents / folder}. List each relevant document, extract the key points, and flag anything that looks like it needs my attention (a deadline, a decision, an inconsistency). If nothing has changed and nothing is time-sensitive, keep it to one line.',
    toolAllowlist: ['listMyDocuments', 'viewDocument', 'extractDocument', 'html'],
    memoryAccess: 'none',
    dailyBudgetCredits: 50,
  },
  {
    id: 'draft-and-propose',
    name: 'Draft & propose',
    emoji: '✍️',
    color: '#EC4899',
    tagline: 'Researches, drafts something for your approval, and asks before finishing.',
    mode: 'propose',
    schedule: null,
    scheduleLabel: 'Manual only',
    brief:
      'When I wake you, research {the task} on the web and draft {what to draft — e.g. a brief, an outline, a reply}. Present the draft as a proposal for me to approve or revise — never treat it as final. If a key decision or detail is ambiguous, ask me one clear question before drafting rather than guessing.',
    toolAllowlist: ['webSearch', 'viewImages', 'html', 'askUserQuestion'],
    memoryAccess: 'none',
    dailyBudgetCredits: 40,
  },
] as const;

export function getAgentTemplate(id: string): AgentTemplate | undefined {
  return AGENT_TEMPLATES.find((t) => t.id === id);
}
